import { appendQueryParameters } from '../../../common/utils/appendQueryParameters';
import { ApplyShopStoreProductWithVariants } from '../../../data/applyshopStore/v3/types';
import { Country } from '../../../data/countries/useCountries';
import { convertIsoToCountryName } from '../../../utils/convertIsoToCountryName';
import { VAS_HUB_TRACKING_SOURCE } from '../constants';

/**
 * Selects the best variant based on preferred ISO country codes (nationality filter)
 * Priority: available variant matching nationality > any matching nationality > first available > first variant
 */
const selectBestVariant = (
  product: ApplyShopStoreProductWithVariants | undefined,
  preferredIsoCodes: string[] = [],
  countries?: Country[],
) => {
  if (!product || !product.variants || product.variants.length === 0) return undefined;

  const variants = product.variants;
  // Convert ISO codes to country names for matching against Shopify variants
  const preferredCountryNames = preferredIsoCodes.map(isoCode =>
    convertIsoToCountryName(isoCode, countries),
  );
  const prefs = new Set(preferredCountryNames.map(c => c.toLowerCase()));

  // 1) Prefer available variant that matches preferred nationality
  const preferredAvailable =
    variants.find(v => prefs.size > 0 && prefs.has(v.country.toLowerCase()) && v.isAvailable) ||
    null;
  if (preferredAvailable) {
    return preferredAvailable;
  }

  // 2) Else any variant that matches preferred nationality (even if not available)
  const preferredAny =
    variants.find(v => prefs.size > 0 && prefs.has(v.country.toLowerCase())) || null;
  if (preferredAny) {
    return preferredAny;
  }

  return null;
};

export const getApplyButtonLink = (
  product: ApplyShopStoreProductWithVariants | undefined,
  userId: string,
  agencyCountry: string,
  isNepalShopifyStoreEnabled: boolean,
  preferredIsoCodes: string[] = [],
  countries?: Country[],
): string => {
  if (!product) {
    return '';
  }

  if (agencyCountry === 'IN' || (agencyCountry === 'NP' && isNepalShopifyStoreEnabled)) {
    return product?.variants?.[0]?.applyShopCartLink
      ? appendQueryParameters(product.variants[0].applyShopCartLink, {
          rp_id: userId,
          source: VAS_HUB_TRACKING_SOURCE,
          discount: `${userId}_HUB_${agencyCountry ?? 'ROW'}`,
        })
      : '';
  } else {
    // Use nationality-aware variant selection for shopify countries
    const selectedVariant = selectBestVariant(product, preferredIsoCodes, countries);
    return selectedVariant?.applyShopCartLink
      ? appendQueryParameters(selectedVariant.applyShopCartLink, {
          rp_id: userId,
          source: VAS_HUB_TRACKING_SOURCE,
          discount: `${userId}_HUB_${agencyCountry ?? 'ROW'}`,
        })
      : appendQueryParameters(product.onlineStoreUrl ?? '', {
          rp_id: userId,
          source: VAS_HUB_TRACKING_SOURCE,
          discount: `${userId}_HUB_${agencyCountry ?? 'ROW'}`,
        });
  }
};
